import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../App';

// ---------------------------------------------------------------------------
// Helper-Funktionen
// ---------------------------------------------------------------------------

function cleanTicketTitle(t) {
  if (!t) return '';
  t = t.replace(/^#\d+\s*/, '');
  t = t.replace(/\([^)]*(?:\/[^)]*)*\)\s*$/, '').trim();
  t = t.replace(/\((?:[^()]*\([^()]*\))*[^()]*\)\s*$/, '').trim();
  return t;
}

function extractTicketTags(title) {
  if (!title) return [];
  const tags = [];
  const catMatch = title.match(/\(([^)]+)\)\s*$/);
  if (catMatch) {
    catMatch[1].split('/').forEach(p => { const s = p.trim(); if (s && s.length < 25) tags.push(s); });
  }
  const kw = [
    [/r[uü]ckruf/i, 'Rückruf'], [/portal[:\s]/i, 'Portal'], [/neuvermietung/i, 'Neuvermietung'],
    [/feucht|schimmel/i, 'Feuchtigkeit'], [/heiz|heizung/i, 'Heizung'], [/wasser|rohr|leck/i, 'Wasser'],
    [/aufzug|fahrstuhl|lift/i, 'Aufzug'], [/schloss|schl[uü]ssel|t[uü]r/i, 'Zugang'],
    [/dach|fassade/i, 'Gebäude'], [/garten|gr[uü]n|baum/i, 'Außenanlage'],
    [/m[uü]ll|entsorgung/i, 'Müll'], [/versicherung|haftpflicht|schaden/i, 'Versicherung'],
    [/eigent[uü]mer|etv|versammlung/i, 'WEG'], [/\btest\b|\[test\]/i, 'Test'],
  ];
  kw.forEach(([re, label]) => { if (re.test(title.toLowerCase()) && !tags.includes(label)) tags.push(label); });
  return tags.slice(0, 2);
}

function normalizeStatus(st) {
  return (st || '').replace(/\d+%$/, '').replace(/\s*\(.*\)\s*$/, '').trim();
}

function parseGermanDate(s) {
  if (!s) return null;
  if (s === 'heute') return new Date();
  const m = s.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null;
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
}

function formatDate(s) {
  if (!s) return null;
  const d = parseGermanDate(s);
  if (!d) return s;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function isOverdue(dateStr, today) {
  const d = parseGermanDate(dateStr);
  if (!d) return false;
  const t = new Date(today);
  t.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < t;
}

function isDueToday(dateStr, today) {
  const d = parseGermanDate(dateStr);
  if (!d) return false;
  const t = new Date(today);
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}

// ---------------------------------------------------------------------------
// Konstanten
// ---------------------------------------------------------------------------

const SORT_OPTIONS = [
  { value: 'remind_date', label: 'WV-Datum' },
  { value: 'due_date', label: 'Fälligkeit' },
  { value: 'updated_at', label: 'Aktualisiert' },
  { value: 'created_at', label: 'Erstellt' },
  { value: 'priority', label: 'Priorität' },
];

const FILTER_FIELDS = [
  { key: 'status', label: 'Status' },
  { key: 'assigned_to', label: 'Sachbearbeiter' },
  { key: 'template', label: 'Vorlage' },
  { key: 'priority', label: 'Priorität' },
];

const COLUMN_COLORS = ['#00a87e', '#eab308', '#8b5cf6', '#10b981', '#ef4444', '#6b7280', '#f97316', '#ec4899'];

const DEFAULT_BOARD = [
  {
    id: 'neue', title: 'Neue Tickets', color: '#00a87e',
    filters: { status: ['neu'] },
    sort: 'updated_at', sortDir: 'desc',
  },
  {
    id: 'meine', title: 'Meine Tickets', color: '#10b981', wide: true,
    filters: { status: ['in Bearbeitung', 'wartend'] },
    sort: 'remind_date', sortDir: 'asc',
  },
  {
    id: 'sanierung', title: 'Sanierung', color: '#8b5cf6',
    filters: { template: ['Sanierungsmaßnahme'] },
    sort: 'due_date', sortDir: 'asc',
  },
];

// ---------------------------------------------------------------------------
// Filter-Logik
// ---------------------------------------------------------------------------

function filterForColumn(col, allTickets) {
  let items = [...allTickets];
  const f = col.filters || {};

  if (f.status?.length) {
    items = items.filter(t => f.status.some(s => normalizeStatus(t.status) === s));
  }
  if (f.assigned_to?.length) {
    items = items.filter(t => {
      const a = (t.assigned_to || '').toLowerCase();
      return f.assigned_to.some(name => a.includes(name.toLowerCase()));
    });
  }
  if (f.template?.length) {
    items = items.filter(t => {
      const tmpl = t.template || '';
      return f.template.some(ft => tmpl.startsWith(ft) || tmpl.includes(ft));
    });
  }
  if (f.priority?.length) {
    items = items.filter(t => f.priority.includes(t.priority));
  }
  if (f.object?.length) {
    items = items.filter(t => {
      const obj = (t.object || '').toLowerCase();
      return f.object.some(o => obj.includes(o.toLowerCase()));
    });
  }

  // Sort
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const sortField = col.sort || 'updated_at';
  const sortMul = (col.sortDir || 'asc') === 'desc' ? -1 : 1;
  const PRIO = { sofort: 1, dringend: 2, hoch: 3, normal: 4, niedrig: 5 };

  items.sort((a, b) => {
    const aO = (parseGermanDate(a.due_date) < today || parseGermanDate(a.remind_date) < today) ? 0 : 1;
    const bO = (parseGermanDate(b.due_date) < today || parseGermanDate(b.remind_date) < today) ? 0 : 1;
    if (aO !== bO) return aO - bO;

    let va, vb;
    if (['remind_date', 'due_date', 'created_at', 'updated_at'].includes(sortField)) {
      va = parseGermanDate(a[sortField]) || new Date(9999, 0);
      vb = parseGermanDate(b[sortField]) || new Date(9999, 0);
    } else if (sortField === 'priority') {
      va = PRIO[a.priority] || 4; vb = PRIO[b.priority] || 4;
    } else {
      va = a[sortField] || ''; vb = b[sortField] || '';
    }
    return va < vb ? -sortMul : va > vb ? sortMul : 0;
  });

  return items;
}

// ---------------------------------------------------------------------------
// Data Hooks
// ---------------------------------------------------------------------------

function useTickets(token) {
  const [tickets, setTickets] = useState([]);
  const [sanierungIds, setSanierungIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [openRes, sanRes] = await Promise.all([
        fetch('/api/tickets/all?filter=all_open', { headers }),
        fetch('/api/tickets/sanierung', { headers }),
      ]);
      if (!openRes.ok) throw new Error(`Tickets: ${openRes.status}`);
      if (!sanRes.ok) throw new Error(`Sanierung: ${sanRes.status}`);
      const [openData, sanData] = await Promise.all([openRes.json(), sanRes.json()]);

      const allOpen = openData.items || [];
      const sanItems = sanData.items || [];
      const ids = new Set(sanItems.map(t => t.id));

      const merged = [...allOpen];
      sanItems.forEach(t => { if (!merged.find(x => x.id === t.id)) merged.push(t); });

      setTickets(merged);
      setSanierungIds(ids);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAll();
    intervalRef.current = setInterval(fetchAll, 3 * 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, [fetchAll]);

  return { tickets, sanierungIds, loading, error, refresh: fetchAll };
}

function useTicketDetail(token, ticketId) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const cacheRef = useRef({});

  useEffect(() => {
    if (!ticketId || !token) { setDetail(null); return; }
    if (cacheRef.current[ticketId]) { setDetail(cacheRef.current[ticketId]); return; }
    setLoadingDetail(true);
    fetch(`/api/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { cacheRef.current[ticketId] = d; setDetail(d); })
      .catch(() => setDetail(null))
      .finally(() => setLoadingDetail(false));
  }, [ticketId, token]);

  const invalidate = useCallback((id) => { delete cacheRef.current[id]; }, []);

  return { detail, loadingDetail, invalidate };
}

function useFilterOptions(token) {
  const [options, setOptions] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch('/api/tickets/filter-options', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => setOptions(data))
      .catch(() => {});
  }, [token]);

  return options;
}

function useConstants(token) {
  const [constants, setConstants] = useState({ status: [], priority: [], assignees: [] });

  useEffect(() => {
    if (!token) return;
    fetch('/api/tickets/constants', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setConstants)
      .catch(() => {});
  }, [token]);

  return constants;
}

function useBoardConfig(token) {
  const [columns, setColumnsState] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [initialLoaded, setInitialLoaded] = useState(false);
  const saveTimerRef = useRef(null);
  const savedIndicatorTimerRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    fetch('/api/tickets/board-config', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.columns?.length) setColumnsState(data.columns);
        else setColumnsState(JSON.parse(JSON.stringify(DEFAULT_BOARD)));
        setInitialLoaded(true);
      })
      .catch(() => {
        setColumnsState(JSON.parse(JSON.stringify(DEFAULT_BOARD)));
        setInitialLoaded(true);
      });
  }, [token]);

  const save = useCallback((newColumns) => {
    setColumnsState(newColumns);
    setSaveStatus('saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (savedIndicatorTimerRef.current) clearTimeout(savedIndicatorTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch('/api/tickets/board-config', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ columns: newColumns }),
        });
        if (res.ok) {
          setSaveStatus('saved');
          savedIndicatorTimerRef.current = setTimeout(() => setSaveStatus('idle'), 1500);
        } else {
          setSaveStatus('error');
        }
      } catch (_) {
        setSaveStatus('error');
      }
    }, 400);
  }, [token]);

  return { columns, setColumns: save, saveStatus, initialLoaded };
}

// ---------------------------------------------------------------------------
// Status / Priority Styles
// ---------------------------------------------------------------------------

function statusDotColor(status) {
  const s = normalizeStatus(status).toLowerCase();
  if (s.includes('neu')) return '#00a87e';
  if (s.includes('bearbeitung')) return '#eab308';
  if (s.includes('wart')) return '#10b981';
  if (s.includes('ausgef')) return '#8b5cf6';
  if (s.includes('abgeschl') || s.includes('abgewiesen')) return '#6b7280';
  return '#94a3b8';
}

function priorityStyle(priority) {
  const p = (priority || '').toLowerCase();
  if (p.includes('sofort')) return { background: '#fee2e2', borderColor: '#dc2626', borderWidth: 3, boxShadow: '0 0 0 2px rgba(220,38,38,0.15)' };
  if (p.includes('dringend')) return { background: '#ffedd5', borderColor: '#fb923c' };
  if (p.includes('hoch')) return { background: '#fef9c3', borderColor: '#facc15' };
  if (p.includes('niedrig')) return { background: '#dcfce7', borderColor: '#86efac' };
  return {};
}

// ---------------------------------------------------------------------------
// TicketCard
// ---------------------------------------------------------------------------

function TicketCard({ ticket, isSelected, onClick, today }) {
  const [hovered, setHovered] = useState(false);
  const tags = extractTicketTags(ticket.title);
  const cleanTitle = cleanTicketTitle(ticket.title);
  const overdue = isOverdue(ticket.due_date, today) || isOverdue(ticket.remind_date, today);
  const pStyle = priorityStyle(ticket.priority);

  const baseStyle = {
    background: pStyle.background || '#ffffff',
    border: `${pStyle.borderWidth || 1}px solid ${pStyle.borderColor || 'rgba(0,0,0,0.06)'}`,
    borderRadius: 8,
    padding: '10px 12px',
    cursor: 'pointer',
    transition: 'all 150ms ease',
    boxShadow: pStyle.boxShadow || (hovered ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'),
    transform: hovered ? 'translateY(-1px)' : 'none',
    borderLeft: overdue ? '3px solid #dc2626' : undefined,
    ...(isSelected ? { borderColor: '#4f46e5', background: '#eef2ff' } : {}),
    ...(hovered && !isSelected ? { borderColor: '#a5b4fc' } : {}),
  };

  return (
    <div
      style={baseStyle}
      onClick={() => onClick(ticket.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {overdue && (
        <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', letterSpacing: '0.05em', marginBottom: 4 }}>
          UBERFÄLLIG
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 5 }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: statusDotColor(ticket.status),
          marginTop: 4, flexShrink: 0,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 500, color: '#191c1f', lineHeight: 1.35,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {cleanTitle}
          </div>
          <div style={{ fontSize: 11, color: '#8d969e', marginTop: 2 }}>#{ticket.id}</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
        {ticket.object && (
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontWeight: 500 }}>
            {ticket.object}
          </span>
        )}
        {tags.map(tag => (
          <span key={tag} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#f3f4f6', color: '#374151', fontWeight: 400 }}>
            {tag}
          </span>
        ))}
        {ticket.remind_date && (
          <span style={{
            fontSize: 10, padding: '1px 6px', borderRadius: 4,
            background: isOverdue(ticket.remind_date, today) ? '#fee2e2' : isDueToday(ticket.remind_date, today) ? '#fef9c3' : '#fefce8',
            color: isOverdue(ticket.remind_date, today) ? '#dc2626' : '#854d0e',
            fontWeight: 500,
          }}>
            WV {formatDate(ticket.remind_date)}
          </span>
        )}
        {ticket.due_date && (
          <span style={{
            fontSize: 10, padding: '1px 6px', borderRadius: 4,
            background: isOverdue(ticket.due_date, today) ? '#fee2e2' : '#f3f4f6',
            color: isOverdue(ticket.due_date, today) ? '#dc2626' : '#505a63',
          }}>
            Fällig {formatDate(ticket.due_date)}
          </span>
        )}
      </div>

      {ticket.assigned_to && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 7 }}>
          <div style={{
            width: 18, height: 18, borderRadius: '50%',
            background: '#e0e7ff', color: '#4f46e5',
            fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {initials(ticket.assigned_to)}
          </div>
          <span style={{ fontSize: 11, color: '#505a63', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ticket.assigned_to}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ColumnPopover
// ---------------------------------------------------------------------------

function ColumnPopover({ col, filterOptions, onToggleFilter, onSetSort, onSetTitle, onSetColor, onToggleWide, onRemove, onClose }) {
  const [objectSearch, setObjectSearch] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({ template: !(col.filters?.template?.length > 0) });

  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const activeFilterCount = Object.values(col.filters || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  const objectOptions = useMemo(() => {
    if (!filterOptions?.object) return [];
    const q = objectSearch.toLowerCase().trim();
    const items = filterOptions.object; // [{value, count}, ...]
    if (!q) return items.slice(0, 15);
    return items.filter(o => (o.value || '').toLowerCase().includes(q)).slice(0, 15);
  }, [filterOptions, objectSearch]);

  const sortButtonStyle = (isActive) => ({
    padding: '3px 8px', borderRadius: 5, border: '1px solid #e5e7eb',
    background: isActive ? '#00a87e' : '#fff',
    color: isActive ? '#fff' : '#374151',
    cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
  });

  const chipStyle = (isSelected) => ({
    padding: '2px 7px', borderRadius: 5, fontSize: 11, cursor: 'pointer',
    border: '1px solid', borderColor: isSelected ? '#00a87e' : '#e5e7eb',
    background: isSelected ? '#00a87e' : '#fff',
    color: isSelected ? '#fff' : '#374151',
    whiteSpace: 'nowrap', fontFamily: 'inherit',
  });

  const sectionLabelStyle = {
    fontSize: 10, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase',
    letterSpacing: '0.4px', marginBottom: 4, display: 'flex', alignItems: 'center',
    gap: 4, cursor: 'pointer', userSelect: 'none',
  };

  return (
    <div
      data-popover="1"
      style={{
        position: 'relative', background: '#fff', border: '1px solid #e5e7eb',
        borderRadius: 10, padding: 12, margin: '-4px 0 8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100,
        maxHeight: '60vh', overflowY: 'auto', fontSize: 12,
      }}
    >
      {/* Sortierung */}
      <div style={{ marginBottom: 10 }}>
        <div style={sectionLabelStyle}>Sortierung</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {SORT_OPTIONS.map(opt => {
            const isActive = col.sort === opt.value;
            const arrow = isActive ? (col.sortDir === 'desc' ? ' \u2193' : ' \u2191') : '';
            return (
              <button
                key={opt.value}
                style={sortButtonStyle(isActive)}
                onClick={() => onSetSort(opt.value)}
              >
                {opt.label}{arrow}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ height: 1, background: '#f3f4f6', margin: '8px 0' }} />

      {/* Filter-Sektionen */}
      {FILTER_FIELDS.map(field => {
        const opts = filterOptions?.[field.key] || [];
        const selected = col.filters?.[field.key] || [];
        const isCollapsed = collapsedSections[field.key];
        const isTemplate = field.key === 'template';

        if (opts.length === 0) return null;

        return (
          <div key={field.key} style={{ marginBottom: 8 }}>
            <div style={sectionLabelStyle} onClick={() => toggleSection(field.key)}>
              <span>{isCollapsed ? '\u25b6' : '\u25bc'}</span>
              <span>{field.label}</span>
              {selected.length > 0 && (
                <span style={{
                  background: '#00a87e', color: '#fff', borderRadius: 8,
                  padding: '0 5px', fontSize: 9, fontWeight: 700,
                }}>
                  {selected.length}
                </span>
              )}
            </div>
            {!isCollapsed && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {opts.map(opt => {
                  const val = typeof opt === 'object' ? (opt.value || opt.label || '') : opt;
                  const label = typeof opt === 'object' ? (opt.label || opt.value || '') : opt;
                  const count = typeof opt === 'object' ? opt.count : null;
                  const isSelected = selected.includes(val);
                  return (
                    <button
                      key={val}
                      style={chipStyle(isSelected)}
                      onClick={() => onToggleFilter(field.key, val)}
                    >
                      {label}{count != null ? ` (${count})` : ''}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Objekt-Sektion */}
      <div style={{ marginBottom: 8 }}>
        <div style={sectionLabelStyle} onClick={() => toggleSection('object')}>
          <span>{collapsedSections['object'] ? '\u25b6' : '\u25bc'}</span>
          <span>Objekt</span>
          {(col.filters?.object?.length > 0) && (
            <span style={{
              background: '#00a87e', color: '#fff', borderRadius: 8,
              padding: '0 5px', fontSize: 9, fontWeight: 700,
            }}>
              {col.filters.object.length}
            </span>
          )}
        </div>
        {!collapsedSections['object'] && (
          <>
            <input
              type="text"
              placeholder="Objekt suchen..."
              value={objectSearch}
              onChange={e => setObjectSearch(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', fontSize: 11, padding: '4px 7px',
                borderRadius: 5, border: '1px solid #e5e7eb', marginBottom: 4,
                fontFamily: 'inherit', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {objectOptions.map(obj => {
                const val = typeof obj === 'object' ? obj.value : obj;
                const count = typeof obj === 'object' ? obj.count : null;
                const display = val.length > 30 ? val.substring(0, 27) + '...' : val;
                const isSelected = (col.filters?.object || []).includes(val);
                return (
                  <button
                    key={val}
                    style={chipStyle(isSelected)}
                    onClick={() => onToggleFilter('object', val)}
                    title={val}
                  >
                    {display}{count != null ? ` (${count})` : ''}
                  </button>
                );
              })}
              {objectOptions.length === 0 && (
                <span style={{ fontSize: 11, color: '#9ca3af' }}>Keine Treffer</span>
              )}
            </div>
          </>
        )}
      </div>

      <div style={{ height: 1, background: '#f3f4f6', margin: '10px 0' }} />

      {/* Spalten-Einstellungen */}
      <div>
        <div style={{ ...sectionLabelStyle, cursor: 'default' }}>Spalte</div>

        {/* Titel */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 3 }}>Titel</div>
          <input
            type="text"
            value={col.title}
            onChange={e => onSetTitle(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '4px 7px',
              borderRadius: 5, border: '1px solid #e5e7eb', fontFamily: 'inherit', outline: 'none',
            }}
          />
        </div>

        {/* Farbe */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 4 }}>Farbe</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {COLUMN_COLORS.map(c => (
              <div
                key={c}
                onClick={() => onSetColor(c)}
                style={{
                  width: 18, height: 18, borderRadius: '50%', background: c, cursor: 'pointer',
                  border: col.color === c ? '2px solid #191c1f' : '2px solid transparent',
                  boxSizing: 'border-box', flexShrink: 0,
                }}
              />
            ))}
          </div>
        </div>

        {/* Breite Spalte */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, color: '#374151' }}>
            <input
              type="checkbox"
              checked={!!col.wide}
              onChange={() => onToggleWide()}
              style={{ cursor: 'pointer' }}
            />
            Breite Spalte
          </label>
        </div>

        {/* Entfernen */}
        <button
          onClick={onRemove}
          style={{
            width: '100%', padding: '5px 0', borderRadius: 6, border: '1px solid #fca5a5',
            background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: 11,
            fontFamily: 'inherit', fontWeight: 500,
          }}
        >
          Spalte entfernen
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TicketColumn
// ---------------------------------------------------------------------------

function TicketColumn({ col, tickets, activePopover, onTogglePopover, filterOptions, onUpdateColumn, onRemoveColumn, selectedId, onCardClick, today }) {
  const sortLabel = SORT_OPTIONS.find(o => o.value === col.sort)?.label || col.sort;
  const filterCount = Object.values(col.filters || {}).reduce((s, a) => s + (a?.length || 0), 0);

  const activeTags = useMemo(() => {
    const tags = [];
    Object.entries(col.filters || {}).forEach(([key, vals]) => {
      if (vals?.length) {
        const label = FILTER_FIELDS.find(f => f.key === key)?.label || key;
        if (vals.length <= 2) {
          vals.forEach(v => tags.push(`${label}: ${v.length > 15 ? v.substring(0, 12) + '...' : v}`));
        } else {
          tags.push(`${label}: ${vals.length} gewählt`);
        }
      }
    });
    return tags;
  }, [col.filters]);

  const handleToggleFilter = (field, value) => onUpdateColumn(col.id, 'toggleFilter', { field, value });
  const handleSetSort = (sortField) => onUpdateColumn(col.id, 'setSort', sortField);
  const handleSetTitle = (title) => onUpdateColumn(col.id, 'setTitle', title);
  const handleSetColor = (color) => onUpdateColumn(col.id, 'setColor', color);
  const handleToggleWide = () => onUpdateColumn(col.id, 'toggleWide', null);

  return (
    <div style={{
      flex: col.wide ? 2 : 1,
      maxWidth: col.wide ? 700 : undefined,
      minWidth: 240,
      background: '#f1f3f8',
      borderRadius: 12,
      border: '1px solid #dde0e8',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Column Header */}
      <div style={{
        padding: '10px 12px 8px',
        borderBottom: activePopover === col.id ? 'none' : '1px solid #dde0e8',
        background: '#eaecf2',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: col.color, flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#505a63', letterSpacing: '0.07em', textTransform: 'uppercase', flex: 1 }}>
            {col.title}
          </span>
          <span style={{ fontSize: 10, color: '#8d969e', whiteSpace: 'nowrap' }}>
            {sortLabel} {col.sortDir === 'desc' ? '\u2193' : '\u2191'}
          </span>
          {filterCount > 0 && (
            <span style={{
              fontSize: 9, padding: '1px 5px', borderRadius: 8,
              background: '#00a87e', color: '#fff', fontWeight: 700,
            }}>
              {filterCount}
            </span>
          )}
          <button
            data-gear="1"
            onClick={() => onTogglePopover(col.id)}
            style={{
              width: 22, height: 22, border: 'none', background: activePopover === col.id ? '#e0e7ff' : 'transparent',
              cursor: 'pointer', borderRadius: 4, color: '#6b7280', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              flexShrink: 0,
            }}
            title="Spalte konfigurieren"
          >
            &#9881;
          </button>
          <span style={{
            fontSize: 11, fontWeight: 700, color: '#fff',
            background: col.color, borderRadius: 10, padding: '1px 7px',
            minWidth: 20, textAlign: 'center', flexShrink: 0,
          }}>
            {tickets.length}
          </span>
        </div>

        {/* Active Filter Tags */}
        {activeTags.length > 0 && activePopover !== col.id && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 5 }}>
            {activeTags.map((tag, i) => (
              <span key={i} style={{
                fontSize: 10, padding: '1px 5px', borderRadius: 4,
                background: '#e0e7ff', color: '#3730a3', fontWeight: 500,
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Popover */}
      {activePopover === col.id && (
        <div style={{ padding: '0 8px', borderBottom: '1px solid #dde0e8' }}>
          <ColumnPopover
            col={col}
            filterOptions={filterOptions}
            onToggleFilter={handleToggleFilter}
            onSetSort={handleSetSort}
            onSetTitle={handleSetTitle}
            onSetColor={handleSetColor}
            onToggleWide={handleToggleWide}
            onRemove={() => onRemoveColumn(col.id)}
            onClose={() => onTogglePopover(null)}
          />
        </div>
      )}

      {/* Column Body */}
      <div style={{
        overflowY: 'auto',
        padding: '8px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        maxHeight: 'calc(100vh - 280px)',
      }}>
        {tickets.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#8d969e', fontSize: 12, padding: '20px 0' }}>
            Keine Tickets
          </div>
        ) : (
          tickets.map(t => (
            <TicketCard
              key={t.id}
              ticket={t}
              isSelected={selectedId === t.id}
              onClick={onCardClick}
              today={today}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TicketDetailPanel
// ---------------------------------------------------------------------------

function germanDateToISO(s) {
  if (!s) return '';
  const m = s.match(/(\d{2})\.(\d{2})\.(\d{4})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

function isoToGermanDate(s) {
  if (!s) return '';
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

function TicketDetailPanel({ ticket, detail, onClose, onStatusChange, onPriorityChange, constants, token }) {
  const [statusChanging, setStatusChanging] = useState(false);
  const [priorityChanging, setPriorityChanging] = useState(false);
  const [comment, setComment] = useState('');
  const [commentSending, setCommentSending] = useState(false);
  const [localStatus, setLocalStatus] = useState(null);
  const [localPriority, setLocalPriority] = useState(null);

  // Edit state
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [localEdits, setLocalEdits] = useState({});
  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState('');
  const [editingInternal, setEditingInternal] = useState(false);
  const [internalValue, setInternalValue] = useState('');

  useEffect(() => {
    setLocalStatus(null);
    setLocalPriority(null);
    setComment('');
    setEditingField(null);
    setLocalEdits({});
    setEditingDesc(false);
    setEditingInternal(false);
  }, [ticket?.id]);

  const currentStatus = localStatus ?? (detail?.status || ticket?.status || '');
  const currentPriority = localPriority ?? (detail?.priority || ticket?.priority || '');

  // PATCH helper
  async function patchTicket(fields) {
    setSaving(true);
    try {
      const res = await fetch(`/api/tickets/${ticket.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      return res.ok;
    } catch (_) {
      return false;
    } finally {
      setSaving(false);
    }
  }

  // Inline edit: start
  function startEdit(field, currentValue) {
    setEditingField(field);
    setEditValue(currentValue || '');
  }

  // Inline edit: save
  async function saveEdit(field, apiField, value) {
    setEditingField(null);
    const sendValue = (field === 'begin_date' || field === 'due_date' || field === 'remind_date')
      ? isoToGermanDate(value) : value;
    if (!sendValue && !localEdits[field]) return;
    const ok = await patchTicket({ [apiField]: sendValue });
    if (ok) {
      setLocalEdits(prev => ({ ...prev, [field]: field.includes('date') ? isoToGermanDate(value) : value }));
    }
  }

  async function handleStatusChange(e) {
    const statusId = e.target.value;
    setStatusChanging(true);
    try {
      const res = await fetch(`/api/tickets/${ticket.id}/status`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status_id: statusId }),
      });
      if (res.ok) {
        const found = constants.status.find(s => s.id === statusId);
        if (found) setLocalStatus(found.label);
        if (onStatusChange) onStatusChange(ticket.id, statusId);
      }
    } catch (_) {}
    setStatusChanging(false);
  }

  async function handlePriorityChange(e) {
    const priorityId = e.target.value;
    setPriorityChanging(true);
    try {
      const res = await fetch(`/api/tickets/${ticket.id}/priority`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority_id: priorityId }),
      });
      if (res.ok) {
        const found = constants.priority.find(p => p.id === priorityId);
        if (found) setLocalPriority(found.label);
        if (onPriorityChange) onPriorityChange(ticket.id, priorityId);
      }
    } catch (_) {}
    setPriorityChanging(false);
  }

  async function handleAssigneeChange(e) {
    const assigneeId = e.target.value;
    const ok = await patchTicket({ assignee_id: assigneeId });
    if (ok) {
      const found = (constants.assignees || []).find(a => a.id === assigneeId);
      if (found) setLocalEdits(prev => ({ ...prev, assigned_to: found.name }));
    }
  }

  async function handleCommentSend() {
    if (!comment.trim()) return;
    setCommentSending(true);
    try {
      await fetch(`/api/tickets/${ticket.id}/comment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: comment.trim(), permission: 'open_simple' }),
      });
      setComment('');
    } catch (_) {}
    setCommentSending(false);
  }

  async function handleDescSave() {
    const ok = await patchTicket({ description: descValue });
    if (ok) {
      setLocalEdits(prev => ({ ...prev, description_html: descValue }));
      setEditingDesc(false);
    }
  }

  async function handleInternalSave() {
    const ok = await patchTicket({ internal_description: internalValue });
    if (ok) {
      setLocalEdits(prev => ({ ...prev, internal_description_html: internalValue }));
      setEditingInternal(false);
    }
  }

  // Current values (local edits override detail/ticket)
  const currentTitle = localEdits.title ?? cleanTicketTitle(detail?.title || ticket?.title || '');
  const currentAssignee = localEdits.assigned_to ?? (detail?.assigned_to || ticket?.assigned_to || '');
  const currentBegin = localEdits.begin_date ?? (detail?.begin_date || ticket?.begin_date || '');
  const currentDue = localEdits.due_date ?? (detail?.due_date || ticket?.due_date || '');
  const currentRemind = localEdits.remind_date ?? (detail?.remind_date || ticket?.remind_date || '');
  const currentDescHtml = localEdits.description_html ?? (detail?.description_html || '');
  const currentInternalHtml = localEdits.internal_description_html ?? (detail?.internal_description_html || '');

  // Match current assignee name to ID for select
  const currentAssigneeId = (constants.assignees || []).find(
    a => a.name.toLowerCase() === currentAssignee.toLowerCase()
  )?.id || '';

  const selectStyle = {
    fontSize: 12, padding: '4px 8px', borderRadius: 6,
    border: '1px solid #dde0e8', background: '#fff', color: '#191c1f',
    cursor: 'pointer', width: '100%',
  };

  const editableStyle = {
    cursor: 'pointer', borderRadius: 4, padding: '1px 4px', margin: '-1px -4px',
    transition: 'background 0.15s',
  };

  const sectionHeaderStyle = {
    fontSize: 11, color: '#8d969e', fontWeight: 600, marginBottom: 6,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  };

  const editBtnStyle = {
    fontSize: 11, color: '#4f46e5', cursor: 'pointer', fontWeight: 500,
    textTransform: 'none', letterSpacing: 'normal', padding: '2px 6px',
    borderRadius: 4, border: '1px solid #c7d2fe', background: '#eef2ff',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(17,24,39,0.35)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#f8f9fc',
          borderRadius: 16,
          maxWidth: 780,
          width: '100%',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        }}
      >
        {/* Panel Header */}
        <div style={{
          background: 'linear-gradient(135deg, #eef2ff 0%, #f0fdf4 100%)',
          padding: '16px 20px',
          borderBottom: '1px solid #dde0e8',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3 }}>#{ticket.id}</div>
            {editingField === 'title' ? (
              <input
                autoFocus
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={() => saveEdit('title', 'title', editValue)}
                onKeyDown={e => { if (e.key === 'Enter') saveEdit('title', 'title', editValue); if (e.key === 'Escape') setEditingField(null); }}
                style={{
                  fontSize: 16, fontWeight: 600, color: '#191c1f', lineHeight: 1.3,
                  width: '100%', border: '1px solid #4f46e5', borderRadius: 6,
                  padding: '4px 8px', background: '#fff', fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            ) : (
              <div
                onClick={() => startEdit('title', currentTitle)}
                title="Klick zum Bearbeiten"
                style={{
                  fontSize: 16, fontWeight: 600, color: '#191c1f', lineHeight: 1.3,
                  cursor: 'pointer', borderRadius: 4, padding: '2px 4px', margin: '-2px -4px',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {currentTitle || '(kein Titel)'}
              </div>
            )}
          </div>
          {saving && <span style={{ fontSize: 11, color: '#4f46e5', whiteSpace: 'nowrap', marginTop: 4 }}>Speichert...</span>}
          <button
            onClick={onClose}
            style={{
              width: 28, height: 28, borderRadius: '50%',
              border: '1px solid #dde0e8', background: '#fff',
              cursor: 'pointer', fontSize: 16, color: '#505a63',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Panel Body */}
        <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }}>

          {/* Info Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 16 }}>
            {/* Status */}
            <div>
              <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3, fontWeight: 500 }}>Status</div>
              <select
                value={currentStatus}
                onChange={handleStatusChange}
                disabled={statusChanging || constants.status.length === 0}
                style={selectStyle}
              >
                {constants.status.length > 0
                  ? constants.status.map(s => <option key={s.id} value={s.id}>{s.label}</option>)
                  : <option>{currentStatus}</option>
                }
              </select>
            </div>

            {/* Priorität */}
            <div>
              <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3, fontWeight: 500 }}>Priorität</div>
              <select
                value={currentPriority}
                onChange={handlePriorityChange}
                disabled={priorityChanging || constants.priority.length === 0}
                style={selectStyle}
              >
                {constants.priority.length > 0
                  ? constants.priority.map(p => <option key={p.id} value={p.id}>{p.label}</option>)
                  : <option>{currentPriority}</option>
                }
              </select>
            </div>

            {/* Zugewiesen an — Dropdown */}
            <div>
              <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3, fontWeight: 500 }}>Zugewiesen an</div>
              {(constants.assignees || []).length > 0 ? (
                <select
                  value={currentAssigneeId}
                  onChange={handleAssigneeChange}
                  disabled={saving}
                  style={selectStyle}
                >
                  <option value="">- nicht zugewiesen -</option>
                  {constants.assignees.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              ) : (
                <div style={{ fontSize: 13, color: '#191c1f', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5',
                    fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {initials(currentAssignee)}
                  </div>
                  {currentAssignee || '-'}
                </div>
              )}
            </div>

            {/* Objekt (read-only) */}
            <div>
              <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3, fontWeight: 500 }}>Objekt</div>
              <div style={{ fontSize: 13, color: '#191c1f' }}>{detail?.object || ticket?.object || '-'}</div>
            </div>

            {/* Vorlage (read-only) */}
            <div>
              <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3, fontWeight: 500 }}>Vorlage</div>
              <div style={{ fontSize: 13, color: '#191c1f' }}>{detail?.template || ticket?.template || '-'}</div>
            </div>

            {/* Erstellt von (read-only) */}
            <div>
              <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3, fontWeight: 500 }}>Erstellt von</div>
              <div style={{ fontSize: 13, color: '#191c1f' }}>{detail?.author || ticket?.initiator || '-'}</div>
            </div>

            {/* Beginn — editable date */}
            <div>
              <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3, fontWeight: 500 }}>Beginn</div>
              {editingField === 'begin_date' ? (
                <input
                  type="date"
                  autoFocus
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={() => saveEdit('begin_date', 'begin_date', editValue)}
                  onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }}
                  style={{ ...selectStyle, cursor: 'text' }}
                />
              ) : (
                <div
                  onClick={() => startEdit('begin_date', germanDateToISO(currentBegin))}
                  title="Klick zum Bearbeiten"
                  style={{ ...editableStyle, fontSize: 13, color: '#191c1f' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {currentBegin || '- setzen -'}
                </div>
              )}
            </div>

            {/* Fällig — editable date */}
            <div>
              <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3, fontWeight: 500 }}>Fällig</div>
              {editingField === 'due_date' ? (
                <input
                  type="date"
                  autoFocus
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={() => saveEdit('due_date', 'due_date', editValue)}
                  onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }}
                  style={{ ...selectStyle, cursor: 'text' }}
                />
              ) : (
                <div
                  onClick={() => startEdit('due_date', germanDateToISO(currentDue))}
                  title="Klick zum Bearbeiten"
                  style={{ ...editableStyle, fontSize: 13, color: '#191c1f' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {currentDue || '- setzen -'}
                </div>
              )}
            </div>

            {/* Wiedervorlage — editable date */}
            <div>
              <div style={{ fontSize: 11, color: '#8d969e', marginBottom: 3, fontWeight: 500 }}>Wiedervorlage</div>
              {editingField === 'remind_date' ? (
                <input
                  type="date"
                  autoFocus
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={() => saveEdit('remind_date', 'remind_date', editValue)}
                  onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }}
                  style={{ ...selectStyle, cursor: 'text' }}
                />
              ) : (
                <div
                  onClick={() => startEdit('remind_date', germanDateToISO(currentRemind))}
                  title="Klick zum Bearbeiten"
                  style={{ ...editableStyle, fontSize: 13, color: '#191c1f' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {currentRemind || '- setzen -'}
                </div>
              )}
            </div>
          </div>

          {/* Öffnen-Link */}
          {(detail?.url || ticket?.url) && (
            <div style={{ marginBottom: 16 }}>
              <a
                href={detail?.url || ticket?.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 13, color: '#4f46e5', textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '5px 12px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff',
                }}
              >
                In Immoware24 öffnen →
              </a>
            </div>
          )}

          {/* Beschreibung — editable */}
          {(currentDescHtml || editingDesc) && (
            <div style={{ marginBottom: 16 }}>
              <div style={sectionHeaderStyle}>
                <span>Beschreibung</span>
                {!editingDesc && (
                  <button
                    onClick={() => { setEditingDesc(true); setDescValue(currentDescHtml || ''); }}
                    style={editBtnStyle}
                  >
                    Bearbeiten
                  </button>
                )}
              </div>
              {editingDesc ? (
                <div>
                  <textarea
                    autoFocus
                    value={descValue}
                    onChange={e => setDescValue(e.target.value)}
                    rows={6}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      fontSize: 13, padding: '8px 10px', borderRadius: 8,
                      border: '1px solid #4f46e5', background: '#fff', color: '#191c1f',
                      resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6,
                    }}
                  />
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={() => setEditingDesc(false)} style={{ ...editBtnStyle, background: '#f3f4f6', borderColor: '#dde0e8', color: '#505a63' }}>
                      Abbrechen
                    </button>
                    <button onClick={handleDescSave} disabled={saving} style={{ ...editBtnStyle, background: '#4f46e5', color: '#fff', borderColor: '#4f46e5' }}>
                      {saving ? 'Speichert...' : 'Speichern'}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  style={{ fontSize: 13, color: '#191c1f', lineHeight: 1.6, padding: '10px 12px', background: '#fff', borderRadius: 8, border: '1px solid #dde0e8' }}
                  dangerouslySetInnerHTML={{ __html: currentDescHtml }}
                />
              )}
            </div>
          )}

          {/* Interne Notiz — editable */}
          {(currentInternalHtml || editingInternal) && (
            <div style={{ marginBottom: 16 }}>
              <div style={sectionHeaderStyle}>
                <span>Interne Notiz</span>
                {!editingInternal && (
                  <button
                    onClick={() => { setEditingInternal(true); setInternalValue(currentInternalHtml || ''); }}
                    style={editBtnStyle}
                  >
                    Bearbeiten
                  </button>
                )}
              </div>
              {editingInternal ? (
                <div>
                  <textarea
                    autoFocus
                    value={internalValue}
                    onChange={e => setInternalValue(e.target.value)}
                    rows={4}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      fontSize: 13, padding: '8px 10px', borderRadius: 8,
                      border: '1px solid #4f46e5', background: '#fff', color: '#374151',
                      resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6,
                    }}
                  />
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={() => setEditingInternal(false)} style={{ ...editBtnStyle, background: '#f3f4f6', borderColor: '#dde0e8', color: '#505a63' }}>
                      Abbrechen
                    </button>
                    <button onClick={handleInternalSave} disabled={saving} style={{ ...editBtnStyle, background: '#4f46e5', color: '#fff', borderColor: '#4f46e5' }}>
                      {saving ? 'Speichert...' : 'Speichern'}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, padding: '10px 12px', background: '#f3f4f6', borderRadius: 8, border: '1px solid #e5e7eb' }}
                  dangerouslySetInnerHTML={{ __html: currentInternalHtml }}
                />
              )}
            </div>
          )}

          {/* Nachrichten */}
          {detail?.nachrichten && detail.nachrichten.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#8d969e', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Nachrichten ({detail.nachrichten.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {detail.nachrichten.map((msg, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #dde0e8', fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, color: '#191c1f', fontSize: 12 }}>{msg.author || msg.von || '-'}</span>
                      <span style={{ color: '#8d969e', fontSize: 11 }}>{msg.date || msg.datum || ''}</span>
                    </div>
                    <div style={{ color: '#374151', lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: msg.text || msg.inhalt || msg.content || '' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Kommentar */}
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, color: '#8d969e', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Kommentar hinzufügen
            </div>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Kommentar eingeben..."
              rows={3}
              style={{
                width: '100%', boxSizing: 'border-box',
                fontSize: 13, padding: '8px 10px', borderRadius: 8,
                border: '1px solid #dde0e8', background: '#fff', color: '#191c1f',
                resize: 'vertical', fontFamily: 'inherit',
              }}
            />
            <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCommentSend}
                disabled={commentSending || !comment.trim()}
                style={{
                  fontSize: 13, padding: '6px 16px', borderRadius: 8,
                  border: 'none', background: '#4f46e5', color: '#fff',
                  cursor: commentSending || !comment.trim() ? 'not-allowed' : 'pointer',
                  opacity: commentSending || !comment.trim() ? 0.6 : 1,
                  fontWeight: 600,
                }}
              >
                {commentSending ? 'Sende...' : 'Senden'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hauptkomponente
// ---------------------------------------------------------------------------

const toolbarBtnStyle = {
  padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)',
  background: 'rgba(255,255,255,0.5)', color: '#505a63', cursor: 'pointer',
  fontSize: 12, fontFamily: 'inherit', fontWeight: 500,
};

export default function Tickets() {
  const { user, token } = useAuth();
  const today = useMemo(() => new Date(), []);

  const { tickets, sanierungIds, loading, error, refresh } = useTickets(token);
  const constants = useConstants(token);
  const filterOptions = useFilterOptions(token);
  const { columns, setColumns, saveStatus } = useBoardConfig(token);

  const [activePopover, setActivePopover] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const { detail: ticketDetail, loadingDetail: ticketLoadingDetail, invalidate: ticketInvalidate } = useTicketDetail(token, selectedId);

  const userName = user?.name || '';

  const isMine = useCallback((ticket) => {
    const a = (ticket.assigned_to || '').toLowerCase();
    return userName.toLowerCase().split(' ').some(part => part.length > 2 && a.includes(part));
  }, [userName]);

  // Close popover on outside click
  useEffect(() => {
    if (!activePopover) return;
    const handler = (e) => {
      if (!e.target.closest('[data-popover]') && !e.target.closest('[data-gear]')) {
        setActivePopover(null);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [activePopover]);

  // Summary counts (global, based on isMine)
  const counts = useMemo(() => {
    const allOpen = tickets.filter(t => {
      const s = normalizeStatus(t.status).toLowerCase();
      return !s.includes('abgeschl') && !s.includes('abgewiesen');
    });
    const mine = allOpen.filter(isMine);
    const overdue = mine.filter(t => isOverdue(t.due_date, today) || isOverdue(t.remind_date, today));
    return {
      open: allOpen.length,
      mine: mine.length,
      overdue: overdue.length,
    };
  }, [tickets, isMine, today]);

  // Column mutation
  const handleUpdateColumn = useCallback((colId, action, payload) => {
    if (!columns) return;
    const newCols = columns.map(c => {
      if (c.id !== colId) return c;
      switch (action) {
        case 'toggleFilter': {
          const { field, value } = payload;
          const filters = { ...c.filters };
          const arr = [...(filters[field] || [])];
          const idx = arr.indexOf(value);
          if (idx >= 0) arr.splice(idx, 1); else arr.push(value);
          if (arr.length) filters[field] = arr; else delete filters[field];
          return { ...c, filters };
        }
        case 'setSort': {
          const sortField = payload;
          if (c.sort === sortField) return { ...c, sortDir: c.sortDir === 'asc' ? 'desc' : 'asc' };
          return { ...c, sort: sortField, sortDir: 'asc' };
        }
        case 'setTitle':
          return { ...c, title: payload };
        case 'setColor':
          return { ...c, color: payload };
        case 'toggleWide':
          return { ...c, wide: !c.wide };
        default:
          return c;
      }
    });
    setColumns(newCols);
  }, [columns, setColumns]);

  const handleTogglePopover = useCallback((colId) => {
    setActivePopover(prev => prev === colId ? null : colId);
  }, []);

  const handleAddColumn = useCallback(() => {
    if (!columns) return;
    const id = 'col-' + Date.now();
    const newCols = [...columns, { id, title: 'Neue Spalte', color: '#6b7280', filters: {}, sort: 'updated_at', sortDir: 'desc' }];
    setColumns(newCols);
    setActivePopover(id);
  }, [columns, setColumns]);

  const handleRemoveColumn = useCallback((colId) => {
    if (!columns || columns.length <= 1) return;
    setColumns(columns.filter(c => c.id !== colId));
    setActivePopover(null);
  }, [columns, setColumns]);

  const handleResetBoard = useCallback(() => {
    if (!window.confirm('Board auf Standard zurücksetzen?')) return;
    setColumns(JSON.parse(JSON.stringify(DEFAULT_BOARD)));
    setActivePopover(null);
  }, [setColumns]);

  // Preset-Modal State
  const [presetModal, setPresetModal] = useState(null); // null | 'save' | 'load'
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [presetSaving, setPresetSaving] = useState(false);
  const [presetFeedback, setPresetFeedback] = useState(null); // { type: 'success'|'error', msg: string }

  const loadPresetList = useCallback(async () => {
    try {
      const res = await fetch('/api/tickets/board-presets', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return [];
      return await res.json() || [];
    } catch (_) { return []; }
  }, [token]);

  const openSaveDialog = useCallback(() => {
    setPresetFeedback(null);
    setPresetName('');
    setPresetModal('save');
  }, []);

  const openLoadDialog = useCallback(async () => {
    setPresetFeedback(null);
    const list = await loadPresetList();
    setPresets(list);
    setPresetModal('load');
  }, [loadPresetList]);

  const closePresetModal = useCallback(() => {
    setPresetModal(null);
    setPresetFeedback(null);
  }, []);

  const handleSavePreset = useCallback(async () => {
    const name = presetName.trim();
    if (!name) {
      setPresetFeedback({ type: 'error', msg: 'Bitte einen Namen eingeben.' });
      return;
    }
    if (name.toLowerCase() === 'default') {
      setPresetFeedback({ type: 'error', msg: '"default" ist reserviert. Bitte anderen Namen wählen.' });
      return;
    }
    setPresetSaving(true);
    try {
      const res = await fetch('/api/tickets/board-presets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, columns }),
      });
      if (res.ok) {
        setPresetFeedback({ type: 'success', msg: `Preset „${name}" gespeichert.` });
        setPresetName('');
        setTimeout(() => { setPresetModal(null); setPresetFeedback(null); }, 1500);
      } else {
        setPresetFeedback({ type: 'error', msg: `Fehler: HTTP ${res.status}` });
      }
    } catch (e) {
      setPresetFeedback({ type: 'error', msg: `Fehler: ${e.message}` });
    }
    setPresetSaving(false);
  }, [columns, token, presetName]);

  const handleLoadPresetByName = useCallback((preset) => {
    setColumns(preset.columns || preset.config?.columns || []);
    setPresetFeedback({ type: 'success', msg: `Preset „${preset.name}" geladen.` });
    setTimeout(() => { setPresetModal(null); setPresetFeedback(null); }, 1000);
  }, [setColumns]);

  const handleDeletePreset = useCallback(async (name) => {
    if (!window.confirm(`Preset „${name}" wirklich löschen?`)) return;
    try {
      const res = await fetch(`/api/tickets/board-presets/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const list = await loadPresetList();
        setPresets(list);
        setPresetFeedback({ type: 'success', msg: `Preset „${name}" gelöscht.` });
      } else {
        setPresetFeedback({ type: 'error', msg: `Fehler: HTTP ${res.status}` });
      }
    } catch (e) {
      setPresetFeedback({ type: 'error', msg: `Fehler: ${e.message}` });
    }
  }, [token, loadPresetList]);

  function handleCardClick(id) {
    setSelectedId(prev => prev === id ? null : id);
  }

  function handleStatusChange(ticketId) {
    ticketInvalidate(ticketId);
  }

  function handlePriorityChange(ticketId) {
    ticketInvalidate(ticketId);
  }

  const selectedTicket = tickets.find(t => t.id === selectedId) || null;

  if (!user) return null;

  // Loading
  if (loading || !columns) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          border: '3px solid #e0e7ff', borderTopColor: '#4f46e5',
          animation: 'spin 0.8s linear infinite',
        }} />
        <div style={{ color: '#8d969e', fontSize: 14 }}>Tickets werden geladen...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 20px', fontFamily: 'var(--font-body, Inter, system-ui, sans-serif)', minHeight: '100vh', background: 'var(--one-surface, #f4f4f4)' }}>

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#191c1f' }}>Ticket-Board</h1>
          <div style={{ fontSize: 12, color: '#8d969e', marginTop: 2 }}>
            {tickets.length} Tickets geladen · Auto-Refresh alle 3 Min.
          </div>
        </div>
        {error && (
          <span style={{ fontSize: 12, color: '#dc2626', background: '#fee2e2', padding: '4px 10px', borderRadius: 6 }}>
            Fehler: {error}
          </span>
        )}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        {/* Summary Bar */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: '#e0e7ff', color: '#4338ca', fontSize: 12, fontWeight: 600 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{counts.open}</span>
            <span style={{ fontWeight: 500 }}>Offen</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 600 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{counts.mine}</span>
            <span style={{ fontWeight: 500 }}>Meine</span>
          </div>
          {counts.overdue > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: '#fee2e2', color: '#991b1b', fontSize: 12, fontWeight: 600 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{counts.overdue}</span>
              <span style={{ fontWeight: 500 }}>Überfällig</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={toolbarBtnStyle} onClick={handleAddColumn}>+ Spalte</button>
          <button style={toolbarBtnStyle} onClick={openSaveDialog}>Speichern</button>
          <button style={toolbarBtnStyle} onClick={openLoadDialog}>Laden</button>
          <button style={toolbarBtnStyle} onClick={handleResetBoard}>Standard</button>
          <button style={toolbarBtnStyle} onClick={refresh}>Aktualisieren</button>
          {saveStatus !== 'idle' && (
            <span
              title={saveStatus === 'error' ? 'Speichern fehlgeschlagen - bitte neu laden' : 'Board-Einstellungen werden gespeichert'}
              style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 10,
                background: saveStatus === 'saved' ? '#d1fae5' : saveStatus === 'error' ? '#fee2e2' : '#eef2ff',
                color: saveStatus === 'saved' ? '#065f46' : saveStatus === 'error' ? '#991b1b' : '#4338ca',
                fontWeight: 500, whiteSpace: 'nowrap',
              }}
            >
              {saveStatus === 'saving' ? '• Speichert…' : saveStatus === 'saved' ? '✓ Gespeichert' : '⚠ Fehler'}
            </span>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <div style={{
        display: 'flex',
        gap: 12,
        padding: 12,
        overflowX: 'auto',
        background: 'var(--one-surface, #f4f4f4)',
        borderRadius: 12,
        minHeight: 400,
        alignItems: 'flex-start',
      }}>
        {columns.map(col => {
          const colTickets = filterForColumn(col, tickets);
          return (
            <TicketColumn
              key={col.id}
              col={col}
              tickets={colTickets}
              activePopover={activePopover}
              onTogglePopover={handleTogglePopover}
              filterOptions={filterOptions}
              onUpdateColumn={handleUpdateColumn}
              onRemoveColumn={handleRemoveColumn}
              selectedId={selectedId}
              onCardClick={handleCardClick}
              today={today}
            />
          );
        })}
      </div>

      {/* Detail-Panel */}
      {selectedId && selectedTicket && (
        <TicketDetailPanel
          ticket={selectedTicket}
          detail={ticketLoadingDetail ? null : ticketDetail}
          onClose={() => setSelectedId(null)}
          onStatusChange={handleStatusChange}
          onPriorityChange={handlePriorityChange}
          constants={constants}
          token={token}
        />
      )}

      {/* Preset-Modal */}
      {presetModal && (
        <div
          onClick={closePresetModal}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(17,24,39,0.35)',
            backdropFilter: 'blur(4px)',
            zIndex: 1100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 14,
              maxWidth: 480, width: '100%',
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
            }}
          >
            <div style={{
              padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'linear-gradient(135deg, #eef2ff 0%, #f0fdf4 100%)',
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#191c1f' }}>
                {presetModal === 'save' ? 'Board-Preset speichern' : 'Board-Preset laden'}
              </div>
              <button
                onClick={closePresetModal}
                style={{ width: 26, height: 26, borderRadius: '50%', border: '1px solid #dde0e8', background: '#fff', cursor: 'pointer', fontSize: 16, color: '#505a63' }}
              >×</button>
            </div>

            <div style={{ padding: '16px 18px', overflowY: 'auto', flex: 1 }}>
              {presetModal === 'save' && (
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10, lineHeight: 1.5 }}>
                    Speichert die aktuelle Spalten-Konfiguration (Filter, Sortierung, Farben) unter einem Namen.
                    Presets bleiben in der Datenbank und überleben App-Updates.
                  </div>
                  <input
                    autoFocus
                    type="text"
                    value={presetName}
                    onChange={e => setPresetName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !presetSaving) handleSavePreset(); }}
                    placeholder={'z.B. Eigene Tickets WV, Sanierung Q2, ...'}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      fontSize: 14, padding: '8px 10px', borderRadius: 8,
                      border: '1px solid #dde0e8', background: '#fff', color: '#191c1f',
                      fontFamily: 'inherit',
                    }}
                  />
                  <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      onClick={closePresetModal}
                      style={{ fontSize: 13, padding: '6px 14px', borderRadius: 8, border: '1px solid #dde0e8', background: '#f3f4f6', color: '#505a63', cursor: 'pointer' }}
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={handleSavePreset}
                      disabled={presetSaving || !presetName.trim()}
                      style={{
                        fontSize: 13, padding: '6px 16px', borderRadius: 8,
                        border: 'none', background: '#4f46e5', color: '#fff',
                        cursor: presetSaving || !presetName.trim() ? 'not-allowed' : 'pointer',
                        opacity: presetSaving || !presetName.trim() ? 0.6 : 1, fontWeight: 600,
                      }}
                    >
                      {presetSaving ? 'Speichert…' : 'Speichern'}
                    </button>
                  </div>
                </div>
              )}

              {presetModal === 'load' && (
                <div>
                  {presets.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#6b7280', padding: '20px 0', textAlign: 'center' }}>
                      Keine gespeicherten Presets vorhanden.<br />
                      <span style={{ fontSize: 12 }}>Klicke „Speichern", um das aktuelle Board zu sichern.</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {presets.map(p => (
                        <div
                          key={p.name}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '10px 12px', border: '1px solid #e5e7eb',
                            borderRadius: 8, background: '#f9fafb',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#191c1f' }}>{p.name}</div>
                            {p.updated_at && (
                              <div style={{ fontSize: 10, color: '#8d969e' }}>
                                {new Date(p.updated_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleLoadPresetByName(p.config || p)}
                            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer', fontWeight: 500 }}
                          >
                            Laden
                          </button>
                          <button
                            onClick={() => handleDeletePreset(p.name)}
                            title="Preset löschen"
                            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {presetFeedback && (
                <div style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 500,
                  background: presetFeedback.type === 'success' ? '#d1fae5' : '#fee2e2',
                  color: presetFeedback.type === 'success' ? '#065f46' : '#991b1b',
                  border: `1px solid ${presetFeedback.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                }}>
                  {presetFeedback.type === 'success' ? '✓ ' : '⚠ '}{presetFeedback.msg}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
